// The Style panel's Sections block: play a tune's sections with a band each,
// as a playback choice (sectionPlan.ts). The score is never touched.
//
// Shown while the Style panel's band-changes choice is By section (BandTransport's
// BandChangesPicker turns sections on for this tune); each section label gets a row
// naming the band it plays (its own, or the tune's) and who comps it, whose ♪
// rests the melody there (a trio's pianist leaving the tune to comp with both
// hands) and whose ✕ hands it back to the tune's band. A section's band is set
// with the band controls themselves: the scope row above the presets and at the
// top of the mixer picks the section they edit (components/bandScope.ts). Edit
// lets you move, relabel, add and remove section starts when the letters or the
// guess are wrong.

import { useMemo, useState } from 'react';
import { describeBandPreset, STYLE_MODIFIED, type StyleBand } from '@/music/style';
import {
  SECTION_CHOICES,
  detectSections,
  printedBarNumber,
  sectionRows,
  writtenBarOf,
  type SectionStart,
} from '@/music/sectionPlan';
import { useStore } from '@/state/store';
import { mergeSections, sectionPlaysMelody } from '@/state/tunePlayback';
import { compSoundLabel } from './soundChoices';
import { TransportControlButton } from './TransportBar';

/** A stored band's nearest preset name, for a readable row. */
function bandLabel(band: StyleBand): string {
  const preset = describeBandPreset(band);
  return preset ? `${preset.label}${preset.modified ? STYLE_MODIFIED : ''}` : 'Custom';
}

/** A bar number that commits on Enter or blur, never mid-typing. */
function BarField({
  value,
  max,
  label,
  testId,
  onCommit,
}: {
  value: number;
  max: number;
  label: string;
  testId: string;
  onCommit: (bar: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    const n = Math.round(Number(draft));
    setDraft(null);
    if (Number.isFinite(n) && n >= 1 && n <= max && n !== value) onCommit(n);
  };
  return (
    <input
      type="number"
      className="section-bar-field"
      min={1}
      max={max}
      value={draft ?? String(value)}
      aria-label={label}
      data-testid={testId}
      onFocus={() => setDraft(String(value))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setDraft(null);
      }}
    />
  );
}

export function SectionPicker() {
  const chart = useStore((s) => s.chart);
  const houseSections = useStore((s) =>
    s.currentTuneKey ? s.houseDefaults[s.currentTuneKey]?.settings.sections : undefined,
  );
  const ownSections = useStore((s) =>
    s.currentTuneKey ? s.tunePlayback[s.currentTuneKey]?.sections : undefined,
  );
  const assign = useStore((s) => s.assignSectionBand);
  const setStarts = useStore((s) => s.setSectionStarts);
  const setMelody = useStore((s) => s.setSectionMelody);
  const [editing, setEditing] = useState(false);

  const measures = chart.measures;
  const detected = useMemo(() => detectSections(chart), [chart]);
  const sections = useMemo(
    () => mergeSections(houseSections, ownSections),
    [houseSections, ownSections],
  );
  const starts: SectionStart[] = useMemo(
    () => sections.starts ?? detected?.starts ?? [],
    [sections.starts, detected],
  );
  const rows = useMemo(() => sectionRows(starts, measures.length), [starts, measures.length]);
  const hasMelody = useMemo(
    () => measures.some((measure) => (measure.melody?.length ?? 0) > 0),
    [measures],
  );
  if (!measures.length || !sections.on) return null;

  const guessed = !sections.starts && !!detected?.guessed;
  const lastPrinted = printedBarNumber(measures, measures.length - 1);
  const labelsInUse = starts.map((start) => start.label);
  const labelChoices = [
    ...SECTION_CHOICES,
    ...labelsInUse.filter((label) => !(SECTION_CHOICES as readonly string[]).includes(label)),
  ].filter((label, index, all) => all.indexOf(label) === index);
  const range = ([from, to]: [number, number]) => {
    const a = printedBarNumber(measures, from);
    const b = printedBarNumber(measures, to);
    return a === b ? `${a}` : `${a}–${b}`;
  };
  const writeStart = (index: number, patch: Partial<SectionStart>) =>
    setStarts(starts.map((start, i) => (i === index ? { ...start, ...patch } : start)));
  const addStart = () => {
    const last = starts[starts.length - 1];
    const taken = new Set(starts.map((start) => start.bar));
    let bar = Math.min(measures.length - 1, (last?.bar ?? -8) + 8);
    while (taken.has(bar) && bar < measures.length - 1) bar += 1;
    if (taken.has(bar)) return;
    setStarts([...starts, { bar, label: last?.label === 'A' ? 'B' : 'A' }]);
  };

  return (
    <div className="section-picker" data-testid="sections">
      {!editing && (
        <>
          {rows.map((row) => {
            const band = sections.bands?.[row.label];
            return (
              <div
                key={row.label}
                className={`feel-cue-row${band ? ' is-assigned' : ''}`}
                data-testid={`section-row-${row.label}`}
              >
                <span className="feel-cue-name section-name">
                  {row.label}
                  <span className="section-bars muted">{row.ranges.map(range).join(', ')}</span>
                </span>
                <span
                  className="feel-cue-state section-state muted"
                  title={
                    band ? `${bandLabel(band)} · ${compSoundLabel(band.compInstrument)}` : undefined
                  }
                >
                  <span data-testid={`section-state-${row.label}`}>
                    {band ? bandLabel(band) : 'Tune’s band'}
                  </span>
                  {band && (
                    <span className="section-plays" data-testid={`section-plays-${row.label}`}>
                      {compSoundLabel(band.compInstrument)}
                    </span>
                  )}
                </span>
                <span className="section-actions">
                  {hasMelody && (
                    <TransportControlButton
                      variant="preset"
                      className="section-melody"
                      data-testid={`section-melody-${row.label}`}
                      aria-pressed={sectionPlaysMelody(sections, row.label)}
                      aria-label={`Melody in ${row.label}`}
                      title={`Melody in ${row.label}`}
                      onClick={() => setMelody(row.label, !sectionPlaysMelody(sections, row.label))}
                    >
                      ♪
                    </TransportControlButton>
                  )}
                  {band && (
                    <TransportControlButton
                      variant="preset"
                      data-testid={`section-clear-${row.label}`}
                      aria-label={`Clear ${row.label}`}
                      title="Back to the tune’s band"
                      onClick={() => assign(row.label)}
                    >
                      ✕
                    </TransportControlButton>
                  )}
                </span>
              </div>
            );
          })}
          {!rows.length && <p className="panel-hint">No sections</p>}
          <div className="section-footer">
            {guessed && (
              <span className="muted" data-testid="sections-guessed">
                Guessed from the changes
              </span>
            )}
            <TransportControlButton
              variant="preset"
              data-testid="sections-edit"
              onClick={() => setEditing(true)}
            >
              Edit
            </TransportControlButton>
          </div>
        </>
      )}
      {editing && (
        <div className="section-editor" data-testid="sections-editor">
          {starts.map((start, index) => (
            <div key={`${start.bar}-${index}`} className="section-edit-row">
              <span className="muted">Bar</span>
              <BarField
                value={printedBarNumber(measures, start.bar)}
                max={lastPrinted}
                label={`Section ${index + 1} starts at bar`}
                testId={`section-start-${index}`}
                onCommit={(printed) => writeStart(index, { bar: writtenBarOf(measures, printed) })}
              />
              <select
                className="bass-style-select"
                value={start.label}
                aria-label={`Section ${index + 1}`}
                data-testid={`section-label-${index}`}
                onChange={(e) => writeStart(index, { label: e.target.value })}
              >
                {labelChoices.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
              <TransportControlButton
                variant="preset"
                data-testid={`section-remove-${index}`}
                aria-label={`Remove section ${index + 1}`}
                disabled={starts.length <= 1}
                onClick={() => setStarts(starts.filter((_, i) => i !== index))}
              >
                ✕
              </TransportControlButton>
            </div>
          ))}
          <div className="section-footer">
            <TransportControlButton variant="preset" data-testid="section-add" onClick={addStart}>
              Add
            </TransportControlButton>
            {sections.starts && (
              <TransportControlButton
                variant="preset"
                data-testid="sections-reset"
                onClick={() => setStarts(null)}
              >
                Reset
              </TransportControlButton>
            )}
            <TransportControlButton
              variant="preset"
              data-testid="sections-done"
              onClick={() => setEditing(false)}
            >
              Done
            </TransportControlButton>
          </div>
        </div>
      )}
    </div>
  );
}
